<template>
  <div id="app">
    <!-- Loading Animation -->
    <div class="loading-overlay" v-if="isLoading" :class="{'fade-out': isReadyToHide}">
      <div class="loading-container">
        <div class="loading-spinner"></div>
        <div class="loading-progress">
          <div class="progress-bar" :style="{ width: loadingProgress + '%' }"></div>
        </div>
        <div class="loading-text">Loading Photo Enhancer...</div>
      </div>
    </div>
    
    <PhotoHeader />
    <ProductIntro />
    <FeatureIntro />
    <FooterComponent />
    <back-to-top />
  </div>
</template>

<script>
import PhotoHeader from './components/PhotoHeader.vue'
import ProductIntro from './components/ProductIntro.vue'
import FeatureIntro from './components/FeatureIntro.vue'
import FooterComponent from './components/FooterComponent.vue'
import BackToTop from './components/BackToTop.vue'

export default {
  name: 'App',
  components: {
    PhotoHeader,
    ProductIntro,
    FeatureIntro,
    FooterComponent,
    'back-to-top': BackToTop
  },
  data() {
    return {
      isLoading: true,
      isReadyToHide: false,
      loadingProgress: 0,
      resourcesLoaded: false,
      minLoadingTime: 2500 // Minimum time to show loading animation (ms)
    }
  },
  mounted() {
    // Start progress animation
    this.animateLoadingProgress();
    
    // Listen for window load event
    window.addEventListener('load', this.onWindowLoaded);
    
    // Minimum loading time
    setTimeout(() => {
      this.resourcesLoaded = true;
      this.checkIfReadyToHide();
    }, this.minLoadingTime);
    
    // Ensure page scrolls to top on refresh
    window.history.scrollRestoration = 'manual';
    window.scrollTo(0, 0);
  },
  methods: {
    animateLoadingProgress() {
      // Simulate progress from 0 to 90% in 2 seconds
      const interval = setInterval(() => {
        if (this.loadingProgress < 90) {
          this.loadingProgress += 1;
        } else if (!this.resourcesLoaded) {
          // Hold at 90% until resources are loaded
          clearInterval(interval);
        }
      }, 20);
    },
    onWindowLoaded() {
      // When resources are loaded, progress to 100%
      this.resourcesLoaded = true;
      this.loadingProgress = 100;
      this.checkIfReadyToHide();
    },
    checkIfReadyToHide() {
      // Only hide loading screen when both criteria are met:
      // 1. Minimum display time has passed
      // 2. Resources are loaded
      if (this.resourcesLoaded && this.loadingProgress >= 100) {
        // Start fade-out animation
        this.isReadyToHide = true;
        
        // Remove loading overlay after animation completes
        setTimeout(() => {
          this.isLoading = false;
        }, 800); // Match this with CSS transition duration
      }
    }
  },
  beforeUnmount() {
    window.removeEventListener('load', this.onWindowLoaded);
  }
}
</script>

<style>
@import './assets/global.css';

#app {
  position: relative;
}

/* Loading Animation Styles */
.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: #ffffff;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  opacity: 1;
  transition: opacity 0.8s ease;
}

.loading-overlay.fade-out {
  opacity: 0;
}

.loading-container {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.loading-spinner {
  width: 60px;
  height: 60px;
  border: 4px solid rgba(74, 107, 255, 0.1);
  border-radius: 50%;
  border-top: 4px solid #4a6bff;
  animation: spin 1s linear infinite;
  margin-bottom: 25px;
}

.loading-progress {
  width: 200px;
  height: 4px;
  background-color: rgba(74, 107, 255, 0.1);
  border-radius: 2px;
  overflow: hidden;
  margin-bottom: 15px;
}

.progress-bar {
  height: 100%;
  background: linear-gradient(90deg, #4a6bff, #2541b2);
  border-radius: 2px;
  transition: width 0.2s ease;
}

.loading-text {
  margin-top: 10px;
  font-size: 18px;
  color: #1a1a2e;
  font-weight: 500;
  letter-spacing: 0.5px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes fadeOut {
  from { opacity: 1; }
  to { opacity: 0; }
}
</style>
